import React, { useContext, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Paper, Stack, Typography, Alert, Divider } from "@mui/material";

import { CodingOpContext } from "../../../context/CodingOpContext/CodingOpContext";

import PatientsFilters from "../components/Worklist/PatientsFilters";
import PatientsTable from "../components/Worklist/PatientsTable";

const CodingOpV3WorklistPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    patients,
    patientsTotalCount,
    patientsFilters,
    setPatientsFilters,
    resetPatientsFilters,
    setPatientsPage,
    setPatientsPageSize,
    refreshPatients,
    loading,
    error,
  } = useContext(CodingOpContext);

  // Refresh patients when coming back from patient detail with changes
  useEffect(() => {
    if (location.state?.refresh) {
      refreshPatients();
      // Clear the state to prevent re-fetching on subsequent renders
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, refreshPatients, navigate, location.pathname]);

  const sortedPatients = useMemo(() => {
    // Defensive: sort by count desc (although backend should already come sorted)
    return [...(patients || [])].sort(
      (a, b) => (b.codingOpCount || 0) - (a.codingOpCount || 0)
    );
  }, [patients]);

  // Navigate to patient detail page
  const handleOpenPatient = (row) => {
    if (row?.cipId) {
      navigate(`/coding-opportunities/patient/${row.cipId}`);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h5" fontWeight={800}>
            Coding Opportunities v3
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Worklist: assigned patients with pending opportunities
          </Typography>
        </Box>

        <Paper sx={{ p: 2 }}>
          <Stack spacing={2}>
            <PatientsFilters
              value={patientsFilters}
              onChange={setPatientsFilters}
              onReset={resetPatientsFilters}
            />

            <Divider />

            {error ? (
              <Alert severity="error">
                Error loading patients. Check console / backend.
              </Alert>
            ) : null}

            <PatientsTable
              rows={sortedPatients}
              loading={loading}
              total={patientsTotalCount}
              page={patientsFilters.page}
              pageSize={patientsFilters.pageSize}
              onPageChange={setPatientsPage}
              onPageSizeChange={setPatientsPageSize}
              onOpenPatient={handleOpenPatient}
            />
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
};

export default CodingOpV3WorklistPage;
